import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, TOKEN_KEY } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [state, setState] = useState({ status: "checking", email: null });

    const verify = useCallback(async () => {
        const token = localStorage.getItem(TOKEN_KEY);
        if (!token) {
            setState({ status: "out", email: null });
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setState({ status: "in", email: data.email });
        } catch (e) {
            localStorage.removeItem(TOKEN_KEY);
            setState({ status: "out", email: null });
        }
    }, []);

    useEffect(() => {
        verify();
        const onLogout = () => setState({ status: "out", email: null });
        window.addEventListener("auth:logout", onLogout);
        return () => window.removeEventListener("auth:logout", onLogout);
    }, [verify]);

    const login = useCallback(async (email, password, remember = false) => {
        const { data } = await api.post("/auth/login", { email, password, remember });
        localStorage.setItem(TOKEN_KEY, data.token);
        setState({ status: "in", email: data.email });
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        setState({ status: "out", email: null });
    }, []);

    return (
        <AuthContext.Provider value={{ ...state, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
